import {
  type DialogStackEntryProjection,
  type DialogStackEntryType,
  type DialogStackPresentation,
  type DialogStackPlaceholderProjection,
  projectDialogStack,
} from './dialog-stack-projection.js'
import { getLayerScrimElement, getLayerStackElement, OVERLAY_LAYER_ATTRIBUTE } from './overlay-host.js'

export type DialogStackEntry = {
  close: () => void
  overlayElement?: HTMLElement | null
  overlayHost?: HTMLElement | null
  stackRegion?: object | null
  regionKind?: 'application' | 'window' | string
  stackIndex?: number
  stackCount?: number
  stackMode?: string
}

export const OVERLAY_HOST_CLASS = 'ae-overlay-host'
export const OVERLAY_HOST_SELECTOR = `.${OVERLAY_HOST_CLASS}`
export const DIALOG_REGION_CLASS = 'ae-dialog-region'
export const DIALOG_REGION_SELECTOR = `.${DIALOG_REGION_CLASS}`
export const DIALOG_LAYER_Z_INDEX = 'var(--ae-dialog-layer-z-index, 20000)'

const OPEN_DIALOGS: DialogStackEntry[] = []
const OVERLAY_HOST_SYNCS = new WeakMap<HTMLElement, { schedule: () => void, resizeObserver?: ResizeObserver }>()

export function getOpenDialogs() {
  return OPEN_DIALOGS
}

export function updateDialogStack() {
  for (const dialog of OPEN_DIALOGS) {
    const regionDialogs = getOpenDialogsForStackEntry(dialog)
    dialog.stackIndex = regionDialogs.indexOf(dialog)
    dialog.stackCount = regionDialogs.length
  }
}

export function getOpenDialogsForStackEntry(dialog: DialogStackEntry) {
  return OPEN_DIALOGS.filter(openDialog => isSameDialogStackRegion(openDialog, dialog))
}

export function registerDialogInStack(dialog: DialogStackEntry) {
  const existingIndex = OPEN_DIALOGS.indexOf(dialog)

  if (existingIndex >= 0) {
    OPEN_DIALOGS.splice(existingIndex, 1)
  }

  OPEN_DIALOGS.push(dialog)
  moveOverlayElementToTop(dialog.overlayElement)
  updateDialogStack()
}

export function unregisterDialogFromStack(dialog: DialogStackEntry) {
  const index = OPEN_DIALOGS.indexOf(dialog)

  if (index === -1) {
    return
  }

  OPEN_DIALOGS.splice(index, 1)
  dialog.stackIndex = -1
  dialog.stackCount = 0
  updateDialogStack()
}

export function closeAllDialogs() {
  for (const dialog of OPEN_DIALOGS.slice().reverse()) {
    dialog.close()
  }

  closeAllLayeredDialogs()
}

export function getNearestDialogRegion(sourceElement: HTMLElement | null | undefined, fallbackRegion?: HTMLElement | null) {
  const nearestRegion = sourceElement?.closest?.(DIALOG_REGION_SELECTOR) as HTMLElement | null

  return nearestRegion || fallbackRegion || null
}

export function getRootDialogRegion(ownerDocument?: Document | null) {
  const resolvedDocument = ownerDocument || (typeof document === 'object' ? document : null)

  if (!resolvedDocument) {
    return null
  }

  return resolvedDocument.querySelector('.ae-root.ae-dialog-region') as HTMLElement
    || resolvedDocument.querySelector('.ae-root') as HTMLElement
    || resolvedDocument.body
}

export function getOrCreateRootOverlayHost(ownerDocument?: Document | null) {
  const host = getOrCreateOverlayHost(getRootDialogRegion(ownerDocument))

  moveOverlayElementToTop(host)

  return host
}

export function getOrCreateOverlayHost(region: HTMLElement | null | undefined) {
  const ownerDocument = region?.ownerDocument || (typeof document === 'object' ? document : null)

  if (!ownerDocument) {
    return null
  }

  const container = region || ownerDocument.body

  if (container.classList?.contains(OVERLAY_HOST_CLASS)) {
    return container
  }

  for (const child of Array.from(container.children)) {
    if (child.classList.contains(OVERLAY_HOST_CLASS)) {
      syncOverlayHostBounds(child as HTMLElement, container)
      return child as HTMLElement
    }
  }

  const host = ownerDocument.createElement('div')
  host.classList.add(OVERLAY_HOST_CLASS, DIALOG_REGION_CLASS)
  host.dataset.aeOverlayHost = 'true'
  host.style.setProperty('position', 'fixed')
  host.style.setProperty('z-index', DIALOG_LAYER_Z_INDEX)
  host.style.setProperty('pointer-events', 'none')
  host.style.setProperty('isolation', 'isolate')
  container.appendChild(host)
  syncOverlayHostBounds(host, container)

  return host
}

export function moveOverlayElementToTop(element: HTMLElement | null | undefined) {
  if (!element?.parentNode) {
    return
  }

  element.parentNode.appendChild(element)
}

function isSameDialogStackRegion(first: DialogStackEntry, second: DialogStackEntry) {
  const firstHost = getDialogStackHost(first)
  const secondHost = getDialogStackHost(second)

  if (firstHost || secondHost) {
    return firstHost === secondHost
  }

  return first.regionKind === second.regionKind
}

function getDialogStackHost(dialog: DialogStackEntry) {
  return dialog.stackRegion || dialog.overlayHost || dialog.overlayElement?.parentElement as HTMLElement | null || null
}

function syncOverlayHostBounds(host: HTMLElement, region: HTMLElement) {
  const ownerDocument = host.ownerDocument
  const ownerWindow = ownerDocument.defaultView
  const isViewportWide = isViewportRegion(region, ownerDocument)

  host.dataset.aeOverlayBounded = isViewportWide ? 'false' : 'true'
  host.style.setProperty('position', 'fixed')
  host.style.setProperty('z-index', DIALOG_LAYER_Z_INDEX)
  host.style.setProperty('pointer-events', 'none')
  host.style.setProperty('isolation', 'isolate')

  if (isViewportWide) {
    host.style.setProperty('inset', '0')
    host.style.removeProperty('left')
    host.style.removeProperty('top')
    host.style.removeProperty('width')
    host.style.removeProperty('height')
    host.style.removeProperty('clip-path')
    host.style.setProperty('overflow', 'visible')
    return
  }

  host.style.setProperty('inset', 'auto')
  host.style.setProperty('overflow', 'hidden')
  host.style.setProperty('clip-path', 'inset(0)')

  const existingSync = OVERLAY_HOST_SYNCS.get(host)
  if (existingSync) {
    existingSync.schedule()
    return
  }

  let animationFrame = 0
  const sync = () => {
    animationFrame = 0

    if (!host.isConnected || !region.isConnected) {
      return
    }

    // Cover only the visible part of the region. When the window scrolls (phones use a single page
    // scroll) the region can be taller than the viewport and start far above it; covering all of it
    // would place dialogs off-screen.
    const bounds = region.getBoundingClientRect()
    const left = Math.max(bounds.left, 0)
    const top = Math.max(bounds.top, 0)
    const right = Math.min(bounds.right, ownerWindow?.innerWidth ?? bounds.right)
    const bottom = Math.min(bounds.bottom, ownerWindow?.innerHeight ?? bounds.bottom)

    host.style.setProperty('left', `${left}px`)
    host.style.setProperty('top', `${top}px`)
    host.style.setProperty('width', `${Math.max(0, right - left)}px`)
    host.style.setProperty('height', `${Math.max(0, bottom - top)}px`)
  }

  const schedule = () => {
    if (animationFrame) {
      return
    }

    if (!ownerWindow?.requestAnimationFrame) {
      sync()
      return
    }

    animationFrame = ownerWindow.requestAnimationFrame(sync)
  }

  const syncState: { schedule: () => void, resizeObserver?: ResizeObserver } = { schedule }
  OVERLAY_HOST_SYNCS.set(host, syncState)

  if (ownerWindow) {
    ownerWindow.addEventListener('resize', schedule)
    ownerWindow.addEventListener('scroll', schedule, true)
  }

  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(schedule)
    resizeObserver.observe(region)
    syncState.resizeObserver = resizeObserver
  }

  sync()
}

function isViewportRegion(region: HTMLElement, ownerDocument: Document) {
  return region === ownerDocument.body
    || region === ownerDocument.documentElement
    || region.classList?.contains('ae-root')
}

/*
 * Layered overlays (feature `layered-overlays`)
 *
 * Each stack layer (dialog, immersive, modal) owns one authoritative logical
 * stack. Its visual state (active/stacked/hidden, depth, placeholder, scrim)
 * is always re-derived from that stack via `projectDialogStack`; entries are
 * never unmounted because they fall out of view.
 */

export type LayeredDialogEntry = {
  close: () => void
  overlayElement?: HTMLElement | null
  stackType: DialogStackEntryType
  stackPresentation: DialogStackPresentation
  requiresScrim: boolean
  backdropClose: boolean
  applyStackProjection(projection: DialogStackEntryProjection): void
  focusStackSurface(): void
}

type LayeredStack = {
  layer: HTMLElement
  entries: LayeredDialogEntry[]
  placeholder: HTMLElement | null
  groupScrims: HTMLElement[]
}

const LAYERED_STACKS = new Map<HTMLElement, LayeredStack>()
const LAYERED_ENTRY_STACKS = new Map<LayeredDialogEntry, LayeredStack>()
const RESTORE_FOCUS_TARGETS = new WeakMap<LayeredDialogEntry, Element | null>()
const FOCUS_GUARDS = new WeakMap<Document, (event: FocusEvent) => void>()
const SCRIM_LISTENERS = new WeakSet<HTMLElement>()
const LAYER_PRIORITY = ['modal', 'immersive', 'dialog']

/** Add (or move to the top) an entry in the stack of the given overlay layer. */
export function registerLayeredDialog(entry: LayeredDialogEntry, layer: HTMLElement) {
  const previousStack = LAYERED_ENTRY_STACKS.get(entry)

  if (previousStack) {
    removeFromStack(previousStack, entry)
  }

  const stack = getOrCreateLayeredStack(layer)
  const ownerDocument = layer.ownerDocument

  if (!RESTORE_FOCUS_TARGETS.has(entry)) {
    RESTORE_FOCUS_TARGETS.set(entry, ownerDocument.activeElement)
  }

  stack.entries.push(entry)
  LAYERED_ENTRY_STACKS.set(entry, stack)

  if (entry.overlayElement) {
    getLayerStackElement(layer).appendChild(entry.overlayElement)
  }

  installFocusGuard(ownerDocument)
  updateLayeredStack(stack)

  if (previousStack && previousStack !== stack) {
    updateLayeredStack(previousStack)
  }
}

/** Remove an entry; the projection is recomputed and focus returns to its opener. */
export function unregisterLayeredDialog(entry: LayeredDialogEntry) {
  const stack = LAYERED_ENTRY_STACKS.get(entry)

  if (!stack) {
    return
  }

  const wasActive = stack.entries[stack.entries.length - 1] === entry
  const restoreFocusTarget = RESTORE_FOCUS_TARGETS.get(entry)

  removeFromStack(stack, entry)
  RESTORE_FOCUS_TARGETS.delete(entry)
  updateLayeredStack(stack)

  if (wasActive) {
    restoreFocus(restoreFocusTarget)
  }
}

/** Re-project the stack an entry belongs to, e.g. after its presentation changed. */
export function refreshLayeredDialog(entry: LayeredDialogEntry) {
  const stack = LAYERED_ENTRY_STACKS.get(entry)

  if (stack) {
    updateLayeredStack(stack)
  }
}

export function isLayeredDialogRegistered(entry: LayeredDialogEntry) {
  return LAYERED_ENTRY_STACKS.has(entry)
}

/** Close every entry logically above `entry` (mixed dialogs and drill-ins alike). */
export function closeLayeredDialogsAbove(entry: LayeredDialogEntry) {
  const stack = LAYERED_ENTRY_STACKS.get(entry)

  if (!stack) {
    return
  }

  const index = stack.entries.indexOf(entry)

  for (const above of stack.entries.slice(index + 1).reverse()) {
    above.close()
  }
}

export function getLayeredDialogs(layer: HTMLElement): readonly LayeredDialogEntry[] {
  return LAYERED_STACKS.get(layer)?.entries ?? []
}

function closeAllLayeredDialogs() {
  for (const stack of Array.from(LAYERED_STACKS.values())) {
    for (const entry of stack.entries.slice().reverse()) {
      entry.close()
    }
  }
}

function getOrCreateLayeredStack(layer: HTMLElement) {
  let stack = LAYERED_STACKS.get(layer)

  if (!stack) {
    stack = { layer, entries: [], placeholder: null, groupScrims: [] }
    LAYERED_STACKS.set(layer, stack)
  }

  const scrim = getLayerScrimElement(layer)

  if (scrim && !SCRIM_LISTENERS.has(scrim)) {
    SCRIM_LISTENERS.add(scrim)
    scrim.addEventListener('click', () => dismissTopEntry(LAYERED_STACKS.get(layer)))
  }

  return stack
}

function removeFromStack(stack: LayeredStack, entry: LayeredDialogEntry) {
  const index = stack.entries.indexOf(entry)

  if (index >= 0) {
    stack.entries.splice(index, 1)
  }

  LAYERED_ENTRY_STACKS.delete(entry)
}

function dismissTopEntry(stack: LayeredStack | undefined) {
  const top = stack?.entries[stack.entries.length - 1]

  if (top?.backdropClose) {
    top.close()
  }
}

function updateLayeredStack(stack: LayeredStack) {
  const projection = projectDialogStack(stack.entries.map(entry => ({
    type: entry.stackType,
    presentation: entry.stackPresentation,
    requiresScrim: entry.requiresScrim,
  })))

  stack.entries.forEach((entry, index) => entry.applyStackProjection(projection.entries[index]))

  const scrim = getLayerScrimElement(stack.layer)

  if (scrim) {
    scrim.dataset.aeScrimVisible = projection.scrimVisible ? 'true' : 'false'
  }

  renderGroupScrims(stack, projection.scrimBefore)
  renderPlaceholder(stack, projection.placeholder)

  if (stack.entries.length === 0) {
    stack.placeholder?.remove()
    stack.placeholder = null
    LAYERED_STACKS.delete(stack.layer)
  }
}

/** Insert scrims in DOM order so a new type dims every surface beneath it. */
function renderGroupScrims(stack: LayeredStack, boundaries: number[]) {
  while (stack.groupScrims.length > boundaries.length) {
    stack.groupScrims.pop()?.remove()
  }

  boundaries.forEach((entryIndex, index) => {
    let scrim = stack.groupScrims[index]
    if (!scrim) {
      scrim = stack.layer.ownerDocument.createElement('div')
      scrim.className = 'ae-overlay-scrim ae-overlay-group-scrim'
      scrim.setAttribute('aria-hidden', 'true')
      scrim.dataset.aeScrimVisible = 'true'
      scrim.addEventListener('click', () => dismissTopEntry(stack))
      stack.groupScrims.push(scrim)
    }

    const surface = stack.entries[entryIndex].overlayElement
    if (surface) {
      surface.parentNode?.insertBefore(scrim, surface)
    }
  })
}

function renderPlaceholder(stack: LayeredStack, placeholder: DialogStackPlaceholderProjection | null) {
  const stackElement = getLayerStackElement(stack.layer)

  if (!placeholder) {
    stack.placeholder?.remove()
    stack.placeholder = null
    return
  }

  if (!stack.placeholder) {
    const ownerDocument = stack.layer.ownerDocument
    const container = ownerDocument.createElement('div')
    const surface = ownerDocument.createElement('div')

    container.setAttribute('aria-hidden', 'true')
    container.setAttribute('inert', '')
    container.appendChild(surface)
    stack.placeholder = container
  }

  const container = stack.placeholder
  const surface = container.firstElementChild as HTMLElement
  const presentation = placeholder.presentation

  // Reuse the dialog visual language: the placeholder renders like one more surface beneath the stack.
  container.className = `ae-dialog__container ae-dialog__backdrop ae-dialog__backdrop--stacked ae-dialog-stack-placeholder${presentation === 'dock' ? ' ae-dialog__backdrop--dock' : ''}`
  container.dataset.aeDialogMode = presentation
  container.dataset.aeCollapsedCount = String(placeholder.hiddenCount)
  container.dataset.aeStackType = stack.entries[placeholder.hiddenCount - 1].stackType
  surface.className = `ae-dialog ae-dialog--stacked ae-dialog--visually-stacked ae-dialog-stack-placeholder__surface${presentation === 'dock' ? ' ae-dialog--dock' : ''}${presentation === 'fullscreen' ? ' ae-dialog--fullscreen' : ''}`
  surface.dataset.aeDialogMode = presentation
  surface.style.setProperty('--ae-dialog-stack-depth', String(placeholder.depth))
  if (stackElement && stackElement.firstElementChild !== container) {
    stackElement.insertBefore(container, stackElement.firstElementChild)
  }
}

function getTopmostActiveEntry() {
  for (const layerName of LAYER_PRIORITY) {
    for (const stack of LAYERED_STACKS.values()) {
      if (stack.layer.getAttribute(OVERLAY_LAYER_ATTRIBUTE) === layerName && stack.entries.length > 0) {
        return stack.entries[stack.entries.length - 1]
      }
    }
  }

  return null
}

function installFocusGuard(ownerDocument: Document) {
  if (FOCUS_GUARDS.has(ownerDocument)) {
    return
  }

  // Keyboard focus may not leave the active dialog for the application or inactive stack entries.
  const guard = (event: FocusEvent) => {
    const active = getTopmostActiveEntry()
    const target = event.target as Element | null

    if (!active?.overlayElement || !target?.closest || active.overlayElement.contains(target)) {
      return
    }

    if (target.closest('.ae-overlay-popups, [data-ae-overlay-layer="toast"], .ae-developer, .ae-overlay-host')) {
      return
    }

    active.focusStackSurface()
  }

  ownerDocument.addEventListener('focusin', guard, true)
  FOCUS_GUARDS.set(ownerDocument, guard)
}

function restoreFocus(target: Element | null | undefined) {
  const active = getTopmostActiveEntry()
  const focusTarget = target as HTMLElement | null

  if (focusTarget?.isConnected && typeof focusTarget.focus === 'function' && (!active?.overlayElement || active.overlayElement.contains(focusTarget))) {
    focusTarget.focus()
    return
  }

  active?.focusStackSurface()
}
